<template>
	<div class="schedule-item" :class="variant" @click="handleClick">
		<div class="schedule-item-title">
			<span class="schedule-item-title-tag">{{ getCalendarName(item.calendarVO.id) }}</span>
			<CTooltip
				v-if="showTooltip"
				:effect="isDark ? 'light' : 'dark'"
				:content="getScheduleThemeName(item)"
				placement="top"
			>
				<span
					class="schedule-item-title-name text-overflow"
					:class="{ 'link-action': showLinkAction && renderLinkAction(item) }"
					>{{ getScheduleThemeName(item) }}</span
				>
			</CTooltip>
			<span
				v-else
				class="schedule-item-title-name text-overflow"
				:class="{ 'link-action': showLinkAction && renderLinkAction(item) }"
				>{{ getScheduleThemeName(item) }}</span
			>
		</div>
		<div class="schedule-item-time">
			<SvgIcons name="calendar_date" class="schedule-item-icon" />
			{{ formatHalfDate(item.scheduleStartTime) }}
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import moment from 'moment';
import { useCalendarCommon } from '@rebuild/materials/shared/customs_calendar/useCalendarCommon';
import { useCurrentReactive } from '@rebuild/materials/shared/customs_calendar/context';
import { getScheduleThemeName } from '@/hooks/calendar/useScheduleCore';

const props = defineProps({
	item: {
		type: Object,
		required: true
	},
	showTooltip: {
		type: Boolean,
		default: false
	},
	showLinkAction: {
		type: Boolean,
		default: false
	},
	variant: {
		type: String,
		default: 'default', // 'default' | 'mobile'
		validator: value => ['default', 'mobile'].includes(value)
	}
});

const emit = defineEmits(['click']);

const { getCalendarName } = useCalendarCommon();
const { handleScheduleAction, renderLinkAction } = useCurrentReactive();

const isDark = computed(() => {
	return getComputedStyle(document.querySelector('body')).getPropertyValue('--theme-color') === '#000000';
});

const formatHalfDate = date => {
	return moment(date).format('YYYY.MM.DD HH:mm');
};

const handleClick = () => {
	handleScheduleAction(props.item);
	emit('click', props.item);
};
</script>

<style lang="less" scoped>
.link-action {
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
}

.schedule-item {
	padding: 12px 8px;
	border-radius: 8px;
	background: rgba(var(--surf-inverse-color-rgb), 0.05);
	&-title {
		padding-bottom: 14px;
		display: flex;
		align-items: center;
		&-tag {
			flex-shrink: 0;
			padding: 2px 4px;
			border-radius: 2px;
			background: rgba(var(--surf-inverse-color-rgb), 0.8);
			color: rgba(var(--surf-theme-color-rgb), 0.7);
			font-size: 10px;
			font-weight: 400;
			height: 18px;
			line-height: 14px;
		}
		&-name {
			width: 0;
			flex: 1;
			padding-left: 4px;
			color: var(--surf-inverse-color);
			font-size: 14px;
			font-weight: 400;
			line-height: 22px; /* 157.143% */
		}
	}
	&-time {
		display: flex;
		align-items: center;
		color: rgba(var(--surf-inverse-color-rgb), 0.4);
		font-size: 12px;
		font-weight: 400;
		line-height: 16px; /* 133.333% */
	}
	&-icon {
		width: 14px;
		height: 14px;
		margin-right: 4px;
		color: rgba(var(--surf-inverse-color-rgb), 0.2) !important;
	}
	&.mobile {
		.schedule-item-title {
			padding-bottom: 8px;
			gap: 4px;
			&-name {
				font-size: 15px;
				line-height: 20px;
			}
		}
	}
}
</style>

